import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { SESSION_KEY } from '../utils/api';

const AuthContext = createContext();
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clear = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!sessionStorage.getItem(SESSION_KEY)) { setLoading(false); return; }
    api.get('/admin-auth/me').then((r) => setUser(r.data)).catch(clear).finally(() => setLoading(false));
  }, [clear]);

  // Client-side idle logout mirrors the server idle timeout (server is authoritative).
  useEffect(() => {
    if (!user) return undefined;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - last > (user.idle_timeout_minutes || 30) * 60000) {
        api.post('/admin-auth/logout').catch(() => {});
        clear();
        window.location.href = '/login';
      }
    }, 30000);
    return () => { clearInterval(timer); ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump)); };
  }, [user, clear]);

  const login = async (email, password) => {
    try {
      const r = await api.post('/admin-auth/login', { email, password });
      sessionStorage.setItem(SESSION_KEY, r.data.session_token);
      return { success: true, mfa: r.data.mfa };
    } catch (e) {
      return { success: false, error: errMsg(e, 'Error al iniciar sesión') };
    }
  };

  const enrollStart = async () => (await api.post('/admin-auth/mfa/enroll/start')).data;

  const enrollConfirm = async (code) => {
    try {
      const r = await api.post('/admin-auth/mfa/enroll/confirm', { code });
      return { success: true, recoveryCodes: r.data.recovery_codes, user: r.data.user };
    } catch (e) {
      return { success: false, error: errMsg(e, 'Código inválido') };
    }
  };

  const verify = async ({ code, recoveryCode }) => {
    try {
      const r = await api.post('/admin-auth/mfa/verify', recoveryCode ? { recovery_code: recoveryCode } : { code });
      setUser(r.data.user);
      return { success: true };
    } catch (e) {
      return { success: false, error: errMsg(e, 'Código inválido') };
    }
  };

  const logout = async () => {
    try { await api.post('/admin-auth/logout'); } catch { /* session may already be gone */ }
    clear();
  };

  // UX only: the backend enforces every permission.
  const can = useCallback((perm) => !!user?.permissions?.includes(perm), [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, enrollStart, enrollConfirm, verify, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe ser usado dentro de AuthProvider');
  return context;
};
